/**
 * EngagementContratDetail — Fiche contrat éditable + IA (résumé, simulation prêt, résiliation).
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Edit3, Save, Sparkles, FileX, Loader2, Trash2, AlertTriangle, Calculator, Banknote, Clock, History, Car, Link2, FileText, Inbox, TrendingUp, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, getCategoryMeta, fmtEur, fmtEurPrecise, fmtDate } from "@/lib/engagements-categories";

export default function EngagementContratDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [form, setForm] = useState({});
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegen] = useState(false);
  const [genLetter, setGenLetter] = useState(false);
  const [showSimu, setShowSimu] = useState(false);
  const [simu, setSimu] = useState(null);
  const [simuLoading, setSimuLoading] = useState(false);
  const [capitalInput, setCapitalInput] = useState("");
  const [syncingFleet, setSyncingFleet] = useState(false);

  const syncFleet = async () => {
    setSyncingFleet(true);
    try {
      const r = await api.post(`/engagements/contrats/${id}/sync-fleet`);
      if (r.data.vehicle_id) {
        toast.success(r.data.vehicle_created ? "Véhicule créé dans Parc Auto" : "Véhicule lié au Parc Auto");
        load();
      } else if (r.data.error) {
        toast.error(r.data.error);
      } else {
        toast.info(r.data.skipped || "Aucune synchronisation");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncingFleet(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/engagements/contrats/${id}`);
      setC(r.data); setForm(r.data);
    } catch (e) { toast.error(apiError(e)); navigate("/app/depenses/contrats"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Bidirectionnel mensuel ↔ annuel (P0)
  const updMensuel = (v) => {
    const num = parseFloat(v);
    setForm(prev => ({
      ...prev,
      montant_mensuel_ht: isNaN(num) ? 0 : num,
      montant_annuel_ht: isNaN(num) ? 0 : Math.round(num * 12 * 100) / 100,
    }));
  };
  const updAnnuel = (v) => {
    const num = parseFloat(v);
    setForm(prev => ({
      ...prev,
      montant_annuel_ht: isNaN(num) ? 0 : num,
      montant_mensuel_ht: isNaN(num) ? 0 : Math.round((num / 12) * 100) / 100,
    }));
  };

  // Ré-extraction montants seulement (P0)
  const [reextracting, setReextracting] = useState(false);
  const reextractAmounts = async () => {
    if (!window.confirm("Ré-extraire les montants depuis le PDF original ?")) return;
    setReextracting(true);
    try {
      const r = await api.post(`/engagements/contrats/${id}/reextract-amounts`);
      toast.success(`Montants ré-extraits : ${r.data.montant_mensuel_ht}€/mois · ${r.data.montant_annuel_ht}€/an`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setReextracting(false); }
  };

  // Upload PDF original (si aucun) ou remplacement
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const uploadPdf = async (file) => {
    if (!file) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/engagements/contrats/${id}/attach-file`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success("PDF attaché");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingPdf(false); }
  };

  // URL du PDF (avec token JWT injecté par api client via Authorization header)
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const openPdf = async () => {
    try {
      const r = await api.get(`/engagements/contrats/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setPdfBlobUrl(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  // Résiliation CLIENT (flux Averdy Agency)
  const [resilClientUploading, setResilClientUploading] = useState(false);
  const uploadResilClient = async (file) => {
    if (!file) return;
    setResilClientUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/engagements/contrats/${id}/demande-resiliation/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const ext = r.data.extracted || {};
      const dateDemande = ext.date_demande_resiliation || "";
      if (!dateDemande) {
        toast.error("Date de demande introuvable dans le PDF — remplissez-la manuellement");
        return;
      }
      if (!window.confirm(`Confirmer la demande ?\n\nDate: ${dateDemande}\nSignataire: ${ext.signataire_demande_resiliation || "?"}\n\nCalcul date d'effet = date demande + 3 mois de préavis`)) return;
      const conf = await api.post(`/engagements/contrats/${id}/demande-resiliation/confirm`, {
        date_demande_resiliation: dateDemande,
        signataire_demande_resiliation: ext.signataire_demande_resiliation,
        reference_ar_resiliation: ext.reference_ar_resiliation,
        duree_preavis_resiliation_mois: 3,
      });
      toast.success(`Résiliation confirmée · Date effet ${conf.data.date_effet_resiliation}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setResilClientUploading(false); }
  };

  // Concurrence intent
  const [concurrenceSent, setConcurrenceSent] = useState(false);
  const triggerConcurrenceIntent = async () => {
    try {
      await api.post("/engagements/concurrence/intent", { contrat_id: id, categorie: c?.categorie });
      setConcurrenceSent(true);
      toast.success("Intérêt enregistré — nos partenaires vous recontacteront");
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { historique, jours_restants, urgence, preavis_statut, ...payload } = form;
      await api.put(`/engagements/contrats/${id}`, payload);
      toast.success("Contrat mis à jour");
      setEdit(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const regen = async () => {
    setRegen(true);
    try {
      const r = await api.post(`/engagements/contrats/${id}/regenerate-summary`);
      setC(prev => ({ ...prev, resume_ia: r.data.resume_ia }));
      toast.success("Résumé régénéré");
    } catch (e) { toast.error(apiError(e)); }
    finally { setRegen(false); }
  };

  const cancelResiliation = async () => {
    if (!window.confirm("Annuler la démarche de résiliation ?\n\nLes lettres brouillons seront supprimées, le contrat repasse en 'actif'. Vous pourrez relancer plus tard si besoin.")) return;
    try {
      const r = await api.post(`/engagements/contrats/${id}/cancel-resiliation`);
      toast.success(`Résiliation annulée · ${r.data.resiliations_supprimees} lettre(s) supprimée(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const generateResiliation = async (force = false) => {
    setGenLetter(true);
    try {
      const r = await api.post(`/engagements/resiliations/generate?contrat_id=${id}${force ? '&force=true' : ''}`);
      toast.success("Lettre générée");
      navigate(`/app/depenses/resiliations?id=${r.data.resiliation_id}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409 && !force) {
        if (window.confirm("Une lettre brouillon existe déjà.\n\nRégénérer et remplacer l'ancienne ?")) {
          return generateResiliation(true);
        }
      } else {
        toast.error(apiError(e));
      }
    }
    finally { setGenLetter(false); }
  };

  const runSimulation = async () => {
    setSimuLoading(true);
    try {
      const capital = capitalInput || c.capital_restant_du || c.capital_emprunte;
      const r = await api.post(`/engagements/contrats/${id}/simulation-remboursement?capital_restant_du=${capital}`);
      setSimu(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSimuLoading(false); }
  };

  const generateEarlyRepayment = async () => {
    setGenLetter(true);
    try {
      const capital = capitalInput || c.capital_restant_du || c.capital_emprunte;
      const r = await api.post(`/engagements/contrats/${id}/demande-remboursement-anticipe?capital_restant_du=${capital}`);
      toast.success("Demande de remboursement générée");
      navigate(`/app/depenses/resiliations?id=${r.data.resiliation_id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenLetter(false); setShowSimu(false); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer définitivement ce contrat ?")) return;
    try {
      await api.delete(`/engagements/contrats/${id}`);
      toast.success("Contrat supprimé");
      navigate("/app/depenses/contrats");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading || !c) return <div className="p-6 text-slate-500">Chargement...</div>;

  const meta = getCategoryMeta(c.categorie);
  const Icon = meta.icon;
  const isPret = ["pret", "credit"].includes(c.categorie);

  return (
    <div className="space-y-6" data-testid="contrat-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/depenses/contrats")}><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
      </div>

      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
        <Card>
          <CardContent className="p-6 flex flex-wrap items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${meta.bg} flex items-center justify-center`}>
              <Icon className={`w-7 h-7 ${meta.color}`} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-xs text-slate-500 uppercase">{meta.label}</p>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{c.prestataire} — {c.nom_contrat}</h1>
              <div className="flex flex-wrap gap-2 mt-1">
                {c.numero_contrat && <Badge variant="outline" className="text-xs">N° {c.numero_contrat}</Badge>}
                {c.reconduction_tacite && <Badge className="bg-purple-100 text-purple-700 text-xs">🔁 Tacite reconduction</Badge>}
                {c.linked_vehicle_id && (
                  <Badge className="bg-blue-100 text-blue-700 text-xs cursor-pointer hover:bg-blue-200" data-testid="linked-vehicle-badge"
                             onClick={(e) => { e.stopPropagation(); navigate(`/app/fleet/vehicles/${c.linked_vehicle_id}`); }}>
                    <Car className="w-3 h-3 mr-1 inline" />Véhicule lié au Parc Auto
                  </Badge>
                )}
                <Badge className={c.urgence === "critique" ? "bg-red-100 text-red-700" : c.urgence === "urgent" ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}>{c.statut || "actif"}</Badge>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold font-mono text-slate-900">{fmtEurPrecise(c.montant_mensuel_ht)}</p>
              <p className="text-xs text-slate-500">/mois · {fmtEur(c.montant_annuel_ht)}/an</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Actions IA */}
      <div className="flex flex-wrap gap-2">
        {!edit && <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-btn"><Edit3 className="w-4 h-4 mr-2" />Modifier</Button>}
        {edit && (<>
          <Button variant="outline" onClick={() => { setEdit(false); setForm(c); }}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="save-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer</Button>
        </>)}
        <Button variant="outline" onClick={regen} disabled={regenerating} data-testid="regen-summary-btn">
          {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Régénérer résumé IA
        </Button>
        {!isPret && c.statut !== "resilier" && (
          <Button onClick={() => generateResiliation(false)} disabled={genLetter} data-testid="gen-resil-btn" className="bg-red-600 hover:bg-red-700">
            {genLetter ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileX className="w-4 h-4 mr-2" />}
            {c.statut === "resiliation_en_cours" ? "Régénérer la lettre" : "Générer lettre résiliation"}
          </Button>
        )}
        {["resiliation_en_cours", "resiliation_confirmee"].includes(c.statut) && (
          <Button onClick={cancelResiliation} variant="outline" data-testid="cancel-resil-btn"
                      className="border-amber-300 text-amber-800 hover:bg-amber-50">
            <FileX className="w-4 h-4 mr-2" />Finalement, ne pas résilier
          </Button>
        )}
        {isPret && (
          <Button onClick={() => setShowSimu(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="simu-btn">
            <Calculator className="w-4 h-4 mr-2" />Simuler remboursement anticipé
          </Button>
        )}
        {c.is_vehicle_contract && !c.linked_vehicle_id && (
          <Button onClick={syncFleet} disabled={syncingFleet} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="sync-fleet-btn">
            {syncingFleet ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}Synchroniser Parc Auto
          </Button>
        )}
        {c.has_pdf_original && (
          <>
            <Button variant="outline" onClick={openPdf} data-testid="view-pdf-btn">
              <FileText className="w-4 h-4 mr-2" />Voir le PDF
            </Button>
            <Button variant="outline" onClick={reextractAmounts} disabled={reextracting} data-testid="reextract-btn">
              {reextracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Ré-extraire montants
            </Button>
          </>
        )}
        {!c.has_pdf_original && (
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-sm font-medium" data-testid="attach-pdf-label">
            {uploadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Attacher un PDF
            <input type="file" accept="application/pdf,image/*" className="hidden"
                        onChange={e => uploadPdf(e.target.files?.[0])} />
          </label>
        )}
        <Button variant="destructive" size="sm" onClick={del} data-testid="delete-btn"><Trash2 className="w-4 h-4 mr-2" />Supprimer</Button>
      </div>

      {/* --- Section CONCURRENCE (spec §4.3) --- */}
      {!isPret && c.categorie && ["assurance","telephonie","leasing","saas","energie","materiel","location","services","abonnement"].includes(c.categorie) && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="concurrence-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-700" />Faire jouer la concurrence</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-3">Signalez votre intention de comparer les offres — nos partenaires vous recontacteront avec des propositions.</p>
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100"
                        onClick={triggerConcurrenceIntent} disabled={concurrenceSent} data-testid="concurrence-btn">
              {concurrenceSent ? <><CheckCircle className="w-4 h-4 mr-2" />Intérêt enregistré</> : <><TrendingUp className="w-4 h-4 mr-2" />Comparer les offres {c.categorie}</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Grille infos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Détails du contrat</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {edit ? (<>
              <div><Label>Prestataire</Label><Input value={form.prestataire || ""} onChange={e => upd("prestataire", e.target.value)} /></div>
              <div><Label>Nom</Label><Input value={form.nom_contrat || ""} onChange={e => upd("nom_contrat", e.target.value)} /></div>
              <div><Label>Catégorie</Label>
                <Select value={form.categorie || "autre"} onValueChange={v => upd("categorie", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(cat => <SelectItem key={cat.value} value={cat.value}>{cat.emoji} {cat.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>N° contrat</Label><Input value={form.numero_contrat || ""} onChange={e => upd("numero_contrat", e.target.value)} /></div>
              <div><Label>Montant mensuel HT</Label><Input type="number" step="0.01" value={form.montant_mensuel_ht || ""} onChange={e => updMensuel(e.target.value)} data-testid="edit-mensuel" /><p className="text-[10px] text-slate-400 mt-1">L'annuel se met à jour</p></div>
              <div><Label>Montant annuel HT</Label><Input type="number" step="0.01" value={form.montant_annuel_ht || ""} onChange={e => updAnnuel(e.target.value)} data-testid="edit-annuel" /><p className="text-[10px] text-slate-400 mt-1">Le mensuel se met à jour</p></div>
              <div><Label>Date début</Label><Input type="date" value={form.date_debut || ""} onChange={e => upd("date_debut", e.target.value)} /></div>
              <div><Label>Date échéance</Label><Input type="date" value={form.date_echeance || ""} onChange={e => upd("date_echeance", e.target.value)} /></div>
              <div><Label>Durée engagement (mois)</Label><Input type="number" value={form.duree_engagement_mois || ""} onChange={e => upd("duree_engagement_mois", parseInt(e.target.value) || 12)} /></div>
              <div><Label>Délai préavis (jours)</Label><Input type="number" value={form.delai_preavis_jours || ""} onChange={e => upd("delai_preavis_jours", parseInt(e.target.value) || 60)} /></div>
              <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <input type="checkbox" id="edit-tacite" checked={!!form.reconduction_tacite} onChange={e => upd("reconduction_tacite", e.target.checked)} className="mt-0.5 w-4 h-4 rounded" data-testid="edit-tacite" />
                <div className="flex-1"><Label htmlFor="edit-tacite" className="cursor-pointer font-medium">🔁 Reconduction tacite</Label><p className="text-xs text-slate-500 mt-0.5">Le contrat repart pour une nouvelle période sauf préavis respecté.</p></div>
              </div>
              {form.reconduction_tacite && (<>
                <div><Label>Durée reconduction (mois)</Label><Input type="number" value={form.duree_reconduction_mois || ""} placeholder="12" onChange={e => upd("duree_reconduction_mois", parseInt(e.target.value) || null)} /></div>
                <div><Label>Préavis reconduction (jours)</Label><Input type="number" value={form.preavis_reconduction_jours || ""} placeholder="60" onChange={e => upd("preavis_reconduction_jours", parseInt(e.target.value) || null)} /></div>
              </>)}
              <div><Label>Email prestataire</Label><Input type="email" value={form.email_prestataire || ""} onChange={e => upd("email_prestataire", e.target.value)} /></div>
              <div><Label>Téléphone</Label><Input value={form.telephone_prestataire || ""} onChange={e => upd("telephone_prestataire", e.target.value)} /></div>
              <div className="md:col-span-2"><Label>Adresse prestataire</Label><Textarea rows={2} value={form.adresse_prestataire || ""} onChange={e => upd("adresse_prestataire", e.target.value)} /></div>
              {isPret && (<>
                <div className="md:col-span-2 border-t pt-3 mt-2"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">💰 Détails prêt / crédit</p></div>
                <div><Label>Capital emprunté (€)</Label><Input type="number" step="0.01" value={form.capital_emprunte || ""} onChange={e => upd("capital_emprunte", parseFloat(e.target.value) || 0)} /></div>
                <div><Label>Capital restant dû (€)</Label><Input type="number" step="0.01" value={form.capital_restant_du || ""} onChange={e => upd("capital_restant_du", parseFloat(e.target.value) || 0)} /></div>
                <div><Label>Taux d'intérêt (%)</Label><Input type="number" step="0.01" value={form.taux_interet || ""} onChange={e => upd("taux_interet", parseFloat(e.target.value) || 0)} /></div>
                <div><Label>TAEG (%)</Label><Input type="number" step="0.01" value={form.taux_effectif_global || ""} onChange={e => upd("taux_effectif_global", parseFloat(e.target.value) || 0)} data-testid="edit-taeg" /></div>
                <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <input type="checkbox" id="edit-remb-ok" checked={!!form.remboursement_anticipe_possible} onChange={e => upd("remboursement_anticipe_possible", e.target.checked)} className="mt-0.5 w-4 h-4 rounded" />
                  <Label htmlFor="edit-remb-ok" className="cursor-pointer">Remboursement anticipé possible</Label>
                </div>
                {form.remboursement_anticipe_possible && (<>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <input type="checkbox" id="edit-remb-gratuit" checked={!!form.remboursement_anticipe_gratuit} onChange={e => upd("remboursement_anticipe_gratuit", e.target.checked)} className="w-4 h-4 rounded" />
                    <Label htmlFor="edit-remb-gratuit" className="cursor-pointer">Sans indemnité (gratuit)</Label>
                  </div>
                  {!form.remboursement_anticipe_gratuit && (
                    <div><Label>Indemnité (%)</Label><Input type="number" step="0.01" value={form.remboursement_anticipe_indemnite_pct || ""} onChange={e => upd("remboursement_anticipe_indemnite_pct", parseFloat(e.target.value) || 0)} /></div>
                  )}
                </>)}
                <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <input type="checkbox" id="edit-ass-emp" checked={!!form.assurance_emprunteur_incluse} onChange={e => upd("assurance_emprunteur_incluse", e.target.checked)} className="mt-0.5 w-4 h-4 rounded" />
                  <Label htmlFor="edit-ass-emp" className="cursor-pointer">Assurance emprunteur incluse</Label>
                </div>
                {form.assurance_emprunteur_incluse && (
                  <div><Label>Coût mensuel assurance emprunteur (€)</Label><Input type="number" step="0.01" value={form.montant_assurance_emprunteur_mensuel || ""} onChange={e => upd("montant_assurance_emprunteur_mensuel", parseFloat(e.target.value) || 0)} /></div>
                )}
              </>)}
            </>) : (<>
              <Info label="Date début" value={fmtDate(c.date_debut)} />
              <Info label="Date échéance" value={fmtDate(c.date_echeance)} />
              <Info label="Durée engagement" value={c.duree_engagement_mois ? `${c.duree_engagement_mois} mois` : "-"} />
              <Info label="Délai préavis" value={c.delai_preavis_jours ? `${c.delai_preavis_jours} jours` : "-"} />
              <Info label="Date limite préavis" value={fmtDate(c.date_limite_preavis)} highlight={c.urgence !== "ok"} />
              <Info label="Prochaine résiliation possible" value={fmtDate(c.date_resiliation_possible)} />
              <Info label="Mode résiliation" value={c.mode_resiliation?.toUpperCase()} />
              <Info label="Reconduction tacite" value={c.reconduction_tacite ? `Oui${c.duree_reconduction_mois ? ' · '+c.duree_reconduction_mois+' mois' : ''}${c.preavis_reconduction_jours ? ' · préavis '+c.preavis_reconduction_jours+'j' : ''}` : "Non"} />
              <Info label="Email prestataire" value={c.email_prestataire || "-"} />
              <Info label="Téléphone" value={c.telephone_prestataire || "-"} />
              <div className="md:col-span-2"><Info label="Adresse" value={c.adresse_prestataire || "-"} /></div>
              {isPret && (<>
                <div className="md:col-span-2 border-t pt-3 mt-2"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">💰 Détails prêt / crédit</p></div>
                <Info label="Capital emprunté" value={fmtEurPrecise(c.capital_emprunte)} />
                <Info label="Capital restant" value={fmtEurPrecise(c.capital_restant_du)} />
                <Info label="Taux d'intérêt" value={c.taux_interet ? `${c.taux_interet}%` : "-"} />
                <Info label="TAEG" value={c.taux_effectif_global ? `${c.taux_effectif_global}%` : "-"} />
                <Info label="Remb. anticipé" value={c.remboursement_anticipe_possible ? (c.remboursement_anticipe_gratuit ? "Possible · GRATUIT" : `Possible · Indemnité ${c.remboursement_anticipe_indemnite_pct ?? "?"}%`) : "Non autorisé"} highlight={c.remboursement_anticipe_gratuit} />
                <Info label="Assurance emprunteur" value={c.assurance_emprunteur_incluse ? `Incluse${c.montant_assurance_emprunteur_mensuel ? ' · '+fmtEurPrecise(c.montant_assurance_emprunteur_mensuel)+'/mois' : ''}` : "Non incluse"} />
              </>)}
            </>)}
          </CardContent>
        </Card>

        {/* Colonne droite : résumé IA + urgence + historique */}
        <div className="space-y-4">
          {c.urgence && c.urgence !== "ok" && c.jours_restants != null && (
            <Card className="border-orange-200 bg-orange-50/40">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  <p className="text-xs font-semibold text-orange-800 uppercase">Alerte préavis</p>
                </div>
                <p className="text-2xl font-bold text-orange-700">{c.jours_restants} jours</p>
                <p className="text-xs text-slate-600 mt-1">avant date limite ({fmtDate(c.date_limite_preavis)})</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-600" />Résumé IA</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-slate-700 whitespace-pre-line">{c.resume_ia || "Aucun résumé disponible"}</p></CardContent>
          </Card>

          {c.clauses_importantes?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Clauses importantes</CardTitle></CardHeader>
              <CardContent>
                <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
                  {c.clauses_importantes.map((cl, i) => <li key={i}>{typeof cl === 'string' ? cl : cl.description || JSON.stringify(cl)}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}

          {c.historique?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4 text-slate-500" />Historique</CardTitle></CardHeader>
              <CardContent>
                <ul className="text-xs text-slate-600 space-y-2">
                  {c.historique.slice(-5).reverse().map((h, i) => (
                    <li key={i} className="flex gap-2">
                      <Clock className="w-3 h-3 mt-0.5 shrink-0" />
                      <div><p className="font-medium">{h.description}</p><p className="text-slate-400">{fmtDate(h.timestamp)} · {h.user_name}</p></div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modal Simulation Prêt */}
      <Dialog open={showSimu} onOpenChange={setShowSimu}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5" />Simulation remboursement anticipé</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Capital restant dû (€)</Label>
              <Input type="number" value={capitalInput} onChange={e => setCapitalInput(e.target.value)} placeholder={c.capital_restant_du || c.capital_emprunte || "0"} data-testid="capital-input" />
            </div>
            <Button onClick={runSimulation} disabled={simuLoading} className="w-full" data-testid="run-simu-btn">
              {simuLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}Calculer
            </Button>
            {simu && (
              <div className="space-y-3 border-t pt-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Info label="Capital restant" value={fmtEurPrecise(simu.capital_restant_du)} />
                  <Info label="IRA légale" value={fmtEurPrecise(simu.indemnite_remboursement_anticipe)} />
                  <Info label="Total à régler" value={fmtEurPrecise(simu.montant_total_a_payer)} highlight />
                  <Info label="Économie nette" value={fmtEurPrecise(simu.economie_nette)} />
                </div>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3"><p className="text-sm text-blue-900">{simu.conseil}</p></CardContent>
                </Card>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSimu(false)}>Fermer</Button>
            {simu && <Button onClick={generateEarlyRepayment} disabled={genLetter} data-testid="gen-remb-btn"><Banknote className="w-4 h-4 mr-2" />Générer lettre de demande</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, highlight = false }) {
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase">{label}</p>
      <p className={`text-sm font-medium ${highlight ? "text-orange-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}
